// src/RootCmp.jsx
import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Navbar } from './cmps/Navbar'
import { Footer } from './cmps/Footer'
import { FooterMobile } from './cmps/FooterMobile'
import { Layout } from './cmps/Layout'
import { AnimatedRoutes } from './cmps/FarmerMotion/AnimatedRoutes'
import { LoginPopup } from './cmps/RegisterLogin/LoginPopup'
import { SignUpPopup } from './cmps/RegisterLogin/SignUpPopup'
import NotRegisteredPopup from './pagesv2/auth/NotRegisteredPopup'
import { Loader } from './cmps/FarmerMotion/Loader'
import { isAdmin, isAdminOrEditor } from './services/permission.service'
import { openLogin, openSignUp } from './store/slices/ui.slice'
import { validateBackendToken } from './store/slices/user.slice'
import { PlusIcon } from './cmps/ui/icons/shared'

/* ------------------------------------------------------------------ */

export function RootCmp() {
  /* ──────────────────── Redux & Location ──────────────────── */
  const dispatch = useDispatch()
  const location = useLocation()
  const isDesktop = useMediaQuery({ minWidth: 1024 })

  /* ──────────────────── Global state (memo‑safe selectors) ──────────────────── */
  const { loggedInUser, isUserChecked } = useSelector(
    (state) => state.userModule,
    (l, r) =>
      l.loggedInUser === r.loggedInUser && l.isUserChecked === r.isUserChecked
  )

  const { showLogin, showSignUp, showNotRegisteredPopup } = useSelector(
    (s) => s.ui,
    (l, r) =>
      l.showLogin === r.showLogin &&
      l.showSignUp === r.showSignUp &&
      l.showNotRegisteredPopup === r.showNotRegisteredPopup
  )

  /* ──────────────────── Privilege helpers ──────────────────── */
  const canCreatePost = isAdminOrEditor(loggedInUser)
  const canEditAnimes = isAdmin(loggedInUser)

  /* -------------------------------------------------------------------------- */
  /*                              ONE‑TIME INIT                                 */
  /* -------------------------------------------------------------------------- */
  const didInitRef = useRef(false)

  useEffect(() => {
    if (didInitRef.current) return
    didInitRef.current = true

    // 1. אימות טוקן – פעם אחת בלבד
    dispatch(validateBackendToken())
  }, [dispatch])

  /* -------------------------------------------------------------------------- */
  /*                     “פתיחת פופ‑אפ” בהתאם ל‑URL                              */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    if (location.pathname === '/login') dispatch(openLogin())
    else if (location.pathname === '/signup') dispatch(openSignUp())
  }, [location.pathname, dispatch])

  /* -------------------------------------------------------------------------- */
  /*                                  UI                                        */
  /* -------------------------------------------------------------------------- */
  if (!isUserChecked) return <Loader />

  return (
    <section className="min-h-screen">
      <Navbar />

      <Layout>
        <main>
          {/* כפתור “הוסף כתבה” */}
          {canCreatePost && location.pathname !== '/create-post' && (
            <div className="fixed bottom-4 right-4 z-50">
              <Link to="/create-post" title="הוסף כתבה לאתר">
                <button className="bg-secondaryBackground border-2 border-primary p-2 rounded-full transition">
                  <PlusIcon />
                </button>
              </Link>
            </div>
          )}

          <AnimatedRoutes
            loggedInUser={loggedInUser}
            isDesktop={isDesktop}
            isAuthorizedAnime={canEditAnimes}
          />
        </main>

        {/* פופאפים גלובליים */}
        {showNotRegisteredPopup && <NotRegisteredPopup />}
        {showLogin && <LoginPopup />}
        {showSignUp && <SignUpPopup />}

        {/* Footer לפי מכשיר */}
        {isDesktop ? <Footer /> : <FooterMobile />}
      </Layout>
    </section>
  )
}
